<template>
   <div class="relative autocomplete-container">
    <!--<div class="relative"> -->
        <Input
            v-if="multiple || !singleSelectedItem"
            :placeholder="placeholder"
            v-model="searchQuery"
            :disabled="disabled"
            @focus="!disabled && (showDropdown = true)"
            @input="() => { loading = true; onInput()}"
            @keydown="handleKeyDown"
            @blur="handleBlur"
            :required="required && (!multiple && !modelValue)"
            autocomplete="off"
        />
        <div v-if="!multiple && singleSelectedItem !== null" class="flex flex-wrap gap-2 mt-2 p-2 cursor-default">
          <span class="bg-gray-100 rounded-md pl-2 pr-1 bg-indigo-400 text-white text-sm hover:bg-indigo-500"
          >
            {{ singleSelectedItem.name }}
            
            <button
                v-if="!disabled"
                type="button"
                class="hover:text-red-500 hover:text-white rounded-full p-1"
                @click="removeItem(singleSelectedItem)"
                >
              ×
            </button>
          </span>
        </div>
        <div v-if="showDropdown && allowNew && !disabled" 
            class="absolute top-11 -left-2 w-full bg-white m-2 rounded-md shadow-md border transition-all duration-300"
            style="z-index: 60;">
            <Loading v-if="loading" class="p-2" />
            <ul v-if="!loading" class=" overflow-y-auto max-h-48">
                <li
                    v-for="(item, index) in itemsLoaded"
                    :key="item.id"
                    @click="selectItem(item)"
                    @mouseenter="activeDropdownIndex = index"
                    class="p-2 cursor-pointer transition-colors"
                    :class="[
                        activeDropdownIndex === index ? 'bg-indigo-100 text-indigo-900 font-medium' : 'bg-white hover:bg-indigo-50',
                        { active: isSelected(item) }
                    ]"
                >
                    {{ item.name }}
                </li>
            </ul>
            <ul>
                <li
                    v-if="allowNew && searchQuery && !itemExists"
                    @click="addNewItem"
                    @mouseenter="activeDropdownIndex = itemsLoaded.length"
                    class="border border-t-1 border-gray-200 p-2 cursor-pointer flex flex-row items-center transition-colors"
                    :class="[
                        activeDropdownIndex === itemsLoaded.length ? 'bg-green-100 text-green-900 font-medium' : 'bg-white hover:bg-green-50'
                    ]"
                >
                    <PlusCircleIcon class="w-6 h-6 text-green-500 cursor-pointer hover:text-green-600 mt-2 mr-2" /> "{{ searchQuery }}"
                </li>
            </ul>
        </div>
    <!-- </div> -->

    <!-- Selected items display for multiple selection -->
    <div v-if="multiple" class="flex flex-wrap gap-2 mt-2 p-2 cursor-default">
      <span
        v-for="item in selectedItems"
        :key="item.id"
        class="bg-gray-100 rounded-md pl-2 pr-1 bg-green-400 text-white text-sm hover:bg-green-500"
      >
        {{ item.name }}
        
        <button
            v-if="!disabled"
            type="button"
            class="hover:text-red-500 hover:text-white rounded-full p-1"
            @click="removeItem(item)"
            >
          ×
        </button>
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';
import {debounce} from '@/service/debounce.js';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: null
  },
  items: {
    type: Array,
    default: () => []
  },
  allowNew: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});
const loading = ref(false);
const itemsLoaded = ref([]);

const emit = defineEmits(['update:modelValue', 'add-new', 'search'])

const searchQuery = ref('')
const showDropdown = ref(false)
const selectedItems = ref([]);
const singleSelectedItem = ref(null);
const activeDropdownIndex = ref(-1);

// Initialize selected items
watch(() => props.modelValue, (newValue) => {
  if (props.multiple) {
    selectedItems.value = newValue || []
  } else {
    singleSelectedItem.value = props.modelValue || null
  }
}, { immediate: true })

watch( () => props.items, (newValue) => {
  loading.value = false;
  itemsLoaded.value = newValue;
  activeDropdownIndex.value = -1;
}, { immediate: true }) 

// const filteredItems = computed(() => {
//   return props.items.filter(item => {
//     const isMatch = item.name.toLowerCase().includes(searchQuery.value.toLowerCase())
//     if (props.multiple) {
//       return isMatch && !selectedItems.value.some(selected => selected.id === item.id)
//     }
//     return isMatch
//   })
// })


const itemExists = computed(() => {
  return props.items.some(item => 
    item.name.toLowerCase() === searchQuery.value.toLowerCase()
  )
})

const isSelected = (item) => {
  if (props.multiple) {
    return selectedItems.value.some(selected => selected.id === item.id)
  }
  return props.modelValue && props.modelValue.id === item.id
}

const selectItem = (item) => {
  if (props.multiple) {
    selectedItems.value = selectedItems.value.filter( s => s.name != item.name)
    const newSelection = [...selectedItems.value, item]
    selectedItems.value = newSelection
    emit('update:modelValue', newSelection)
  } else {
    singleSelectedItem.value = item;
    emit('update:modelValue', item)
  }
  searchQuery.value = ''
  showDropdown.value = false
  itemsLoaded.value = [];
  activeDropdownIndex.value = -1;
}

const removeItem = (item) => {
  if (props.multiple) {
    const newSelection = selectedItems.value.filter(i => i.name !== item.name)
    selectedItems.value = newSelection
    emit('update:modelValue', newSelection)
  } else {
    singleSelectedItem.value = null;
    emit('update:modelValue', singleSelectedItem.value)
  }
  
}


const addNewItem = () => {
  if (searchQuery.value.trim() !== '') {
    emit('add-new', searchQuery.value.trim())
    const model = {name: searchQuery.value.trim()}
    if (!props.multiple) {
      singleSelectedItem.value = model;
    }
    selectItem(model);
    searchQuery.value = ''
    showDropdown.value = false
    activeDropdownIndex.value = -1;
  }
}

const handleKeyDown = (e) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (!showDropdown.value) {
      showDropdown.value = true;
      activeDropdownIndex.value = 0;
      return;
    }
    const totalCount = (itemsLoaded.value?.length || 0) + (props.allowNew && searchQuery.value && !itemExists.value ? 1 : 0);
    if (totalCount > 0) {
      activeDropdownIndex.value = (activeDropdownIndex.value + 1) % totalCount;
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (showDropdown.value) {
      const totalCount = (itemsLoaded.value?.length || 0) + (props.allowNew && searchQuery.value && !itemExists.value ? 1 : 0);
      if (totalCount > 0) {
        activeDropdownIndex.value = (activeDropdownIndex.value - 1 + totalCount) % totalCount;
      }
    }
  } else if (e.key === 'Enter') {
    e.preventDefault();
    handleEnterKey();
  } else if (e.key === 'Escape') {
    showDropdown.value = false;
    activeDropdownIndex.value = -1;
  }
};

const handleEnterKey = () => {
  const query = (searchQuery.value || '').trim();
  if (!query) return;

  const totalLoaded = itemsLoaded.value?.length || 0;
  if (activeDropdownIndex.value >= 0 && activeDropdownIndex.value < totalLoaded) {
    selectItem(itemsLoaded.value[activeDropdownIndex.value]);
    return;
  }
  if (activeDropdownIndex.value === totalLoaded && props.allowNew && !itemExists.value) {
    addNewItem();
    return;
  }

  if (itemsLoaded.value && itemsLoaded.value.length > 0) {
    const exactMatch = itemsLoaded.value.find(
      item => item.name?.toLowerCase() === query.toLowerCase()
    );
    if (exactMatch) {
      selectItem(exactMatch);
      return;
    }
    // Select the first suggested item
    selectItem(itemsLoaded.value[0]);
    return;
  }

  if (props.allowNew) {
    addNewItem();
  }
};

const handleBlur = () => {
  setTimeout(() => {
    const query = (searchQuery.value || '').trim();
    if (props.allowNew && query && !singleSelectedItem.value && !props.multiple) {
      handleEnterKey();
    }
    showDropdown.value = false;
    activeDropdownIndex.value = -1;
  }, 200);
};

const onInput = debounce(() => {
  showDropdown.value = true
  activeDropdownIndex.value = -1
  emit('search', searchQuery.value)
}, 300)

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.autocomplete-container')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  // Add and remove click outside listener
  document.addEventListener('click', handleClickOutside)
  if (props.multiple){
    selectedItems.value = props.modelValue || []
  } else {
    singleSelectedItem.value = props.modelValue || null
  }
  itemsLoaded.value = props.items;
  if (props.multiple) {
    selectedItems.value = props.modelValue || []
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
